/**
 * Visit Replay (prototype) — scrub through a reconstruction of one visit:
 * stage chapters, room presence, audio coverage, the normalized moment
 * stream, and the words being said at the scrubbed instant. Rebuilt from the
 * visit's current rows, not an audit log — later edits/deletions aren't
 * preserved. Reached at `/replay?visit=<uuid>`.
 */
import { useSessionStore } from "@auth/sessionStore";
import { AsyncBoundary } from "@components/AsyncBoundary";
import { CompanionHeader } from "@components/CompanionHeader";
import { useUser } from "@context/UserContext";
import {
  faBackwardStep,
  faChevronLeft,
  faForwardStep,
  faPause,
  faPlay,
  faRotateLeft,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  cn,
  IconButton,
  PanelError,
  SegmentedControl,
  Skeleton,
} from "@scribenote/brian-design-system";
import { Navigate, useSearch } from "@tanstack/react-router";
import { canAccessReplay } from "@users/userClient";
import { formatDate, formatTimeRange } from "@utils/format-date";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";

import { MomentFeed } from "./MomentFeed";
import {
  DEFAULT_OPEN_STAY_HOURS,
  MAX_OPEN_STAY_HOURS,
  type MomentCategory,
  type VisitReplay,
} from "./replayClient";
import { useVisitReplay } from "./replayQueries";
import { ALL_CATEGORIES, CATEGORY_INK, CATEGORY_LABEL } from "./replayTheme";
import { ReplayTimeline } from "./ReplayTimeline";
import { buildVisitScale, ms } from "./timeScale";
import { TranscriptPanel } from "./TranscriptPanel";

const SPEEDS = [15, 60, 240] as const;
const SPEED_OPTIONS = SPEEDS.map((s) => ({ value: s, label: `${s}×` }));

const ReplaySkeleton = () => (
  <div className="flex flex-col gap-4 p-6">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-40" />
    <Skeleton className="h-64" />
  </div>
);

export const ReplayShell = () => {
  const { visit } = useSearch({ from: "/replay" });
  const { currentUser } = useUser();

  if (!currentUser) return null;
  if (!canAccessReplay(currentUser)) return <Navigate to="/" />;

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-paper font-body text-ink-body">
      {visit ? (
        <AsyncBoundary
          skeleton={<ReplaySkeleton />}
          fallback={(retry) => (
            <PanelError label="visit replay" onRetry={retry} />
          )}
          resetKeys={[visit]}
        >
          {/* Keyed so playhead/playing/filter state never survives a ?visit
              change — a stale playhead from visit A can sit outside B's window. */}
          <ReplayInner key={visit} visitUuid={visit} />
        </AsyncBoundary>
      ) : (
        <>
          <CompanionHeader
            back={{ title: "Visit Replay", testId: "replay-back-empty" }}
          />
          <div className="flex flex-1 items-center justify-center">
            <span className="text-sm text-annotation">
              No visit selected. Open a replay from a visit's card, or pass
              ?visit=&lt;uuid&gt;.
            </span>
          </div>
        </>
      )}
    </div>
  );
};

const currentStageName = (replay: VisitReplay, t: number): string | null => {
  let name: string | null = null;
  for (const stage of replay.stages) {
    if (stage.enteredAt && ms(stage.enteredAt) <= t) name = stage.name;
  }
  return name;
};

const currentRoomName = (replay: VisitReplay, t: number): string | null => {
  for (const stay of replay.stays) {
    const left = stay.leftAt ? ms(stay.leftAt) : Number.POSITIVE_INFINITY;
    if (ms(stay.enteredAt) <= t && t < left) return stay.roomName;
  }
  return null;
};

const ReplayInner = ({ visitUuid }: { visitUuid: string }) => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const [openStayHours, setOpenStayHours] = useState(DEFAULT_OPEN_STAY_HOURS);
  // Extending re-requests the replay under a transition so the mounted tree
  // (and the playhead position with it) survives the suspense refetch.
  const [extending, startExtending] = useTransition();
  const { data: replay } = useVisitReplay(sessionKey, visitUuid, openStayHours);

  const extendOpenStay = () =>
    startExtending(() => {
      setOpenStayHours((h) => Math.min(h + 24, MAX_OPEN_STAY_HOURS));
    });

  const start = ms(replay.windowStart);
  const end = ms(replay.windowEnd);

  const scale = useMemo(() => buildVisitScale(replay), [replay]);

  const [playhead, setPlayhead] = useState(start);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(60);
  const [visibleCategories, setVisibleCategories] = useState<
    Set<MomentCategory>
  >(new Set(ALL_CATEGORIES));
  const [transcriptOpen, setTranscriptOpen] = useState(true);

  const playheadRef = useRef(playhead);
  useEffect(() => {
    playheadRef.current = playhead;
  }, [playhead]);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      let next = playheadRef.current + 80 * speed;
      // Jump over collapsed idle gaps — nothing happens in them, and at 240×
      // a 3-day gap would otherwise take ~18 minutes of wall time.
      next = scale.gapEnd(next) ?? next;
      if (next >= end) {
        setPlayhead(end);
        setPlaying(false);
      } else {
        setPlayhead(next);
      }
    }, 80);
    return () => clearInterval(id);
  }, [playing, speed, end, scale]);

  const atEnd = playhead >= end;
  const togglePlay = () => {
    if (atEnd) {
      setPlayhead(start);
      setPlaying(true);
    } else {
      setPlaying(!playing);
    }
  };

  const toggleCategory = (category: MomentCategory) => {
    const next = new Set(visibleCategories);
    if (next.has(category) && next.size > 1) next.delete(category);
    else next.add(category);
    setVisibleCategories(next);
  };

  const moments = replay.moments.filter((m) =>
    visibleCategories.has(m.category),
  );

  const seekTo = (t: number) => {
    setPlaying(false);
    setPlayhead(Math.min(Math.max(t, start), end));
  };
  // Step between visible events. The 250ms slack keeps repeated presses
  // walking instead of re-landing on the moment we're already parked on.
  const prevEvent = () => {
    for (let i = moments.length - 1; i >= 0; i--) {
      const t = ms(moments[i].at);
      if (t < playhead - 250) return seekTo(t);
    }
    seekTo(start);
  };
  const nextEvent = () => {
    const next = moments.find((m) => ms(m.at) > playhead + 250);
    seekTo(next ? ms(next.at) : end);
  };

  const stageName = currentStageName(replay, playhead);
  const roomName = currentRoomName(replay, playhead);
  // "Empty" is the detectable case; a sparse-but-pruned lane can't be told
  // apart from a genuinely quiet one, so no note is shown there. Holds the
  // cutoff date when the note applies, null otherwise.
  const pimsPrunedSince =
    replay.pimsHistoryAvailableSince !== null &&
    start < ms(replay.pimsHistoryAvailableSince) &&
    !replay.moments.some((m) => m.category === "pims")
      ? replay.pimsHistoryAvailableSince
      : null;
  const disclosures =
    replay.openStayCapped ||
    !replay.transcriptAttributionComplete ||
    pimsPrunedSince !== null;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <CompanionHeader
        back={{
          title: replay.visit.patientName ?? "Visit Replay",
          testId: "replay-back",
        }}
      />

      {/* Visit info + playback controls */}
      <div className="flex items-center gap-3 border-b border-rule px-4 py-3">
        <div className="min-w-0 flex-1">
          <p className="truncate font-display text-sm font-medium text-ink">
            {replay.visit.visitType}
            {replay.visit.reason ? ` — ${replay.visit.reason}` : ""}
          </p>
          <p className="text-xs text-annotation">
            {replay.visit.clientName ?? "No client"} ·{" "}
            {replay.visit.assignedUserName ?? "Unassigned"} ·{" "}
            {formatTimeRange(replay.windowStart, replay.windowEnd)} ·{" "}
            {moments.length === replay.moments.length
              ? `${replay.moments.length} moments`
              : `${moments.length} of ${replay.moments.length} moments`}
            <span title="Rebuilt from the visit's current records — later edits or deletions aren't preserved.">
              {" "}
              · reconstructed
            </span>
            {replay.turnsTruncated && (
              <span className="text-ink-flag"> · transcript truncated</span>
            )}
          </p>
        </div>
        <SegmentedControl
          aria-label="Playback speed"
          options={SPEED_OPTIONS}
          value={speed}
          onValueChange={setSpeed}
          size="sm"
          variant="ghost"
          mono
        />
        <IconButton
          size="sm"
          onClick={prevEvent}
          aria-label="Previous event"
          title="Previous event"
        >
          <FontAwesomeIcon icon={faBackwardStep} aria-hidden="true" />
        </IconButton>
        <Button
          variant="primary"
          size="sm"
          data-testid="replay-play"
          onClick={togglePlay}
        >
          <FontAwesomeIcon
            icon={atEnd ? faRotateLeft : playing ? faPause : faPlay}
          />
          {atEnd ? "Replay" : playing ? "Pause" : "Play"}
        </Button>
        <IconButton
          size="sm"
          onClick={nextEvent}
          aria-label="Next event"
          title="Next event"
        >
          <FontAwesomeIcon icon={faForwardStep} aria-hidden="true" />
        </IconButton>
      </div>

      {/* Known gaps in the payload — disclosed, never silently absorbed.
          Flag ink: each line needs a look, but nothing has stopped. */}
      {disclosures && (
        <div className="flex flex-col gap-0.5 border-b border-rule px-4 py-2 text-xs text-ink-flag">
          {replay.openStayCapped && (
            <span>
              Recordings beyond the first {openStayHours} hours of this
              visit&rsquo;s open room stay aren&rsquo;t shown.
              {openStayHours < MAX_OPEN_STAY_HOURS && (
                <button
                  type="button"
                  onClick={extendOpenStay}
                  disabled={extending}
                  data-testid="replay-extend-open-stay"
                  className="ml-1.5 cursor-pointer rounded-paper font-medium underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:cursor-not-allowed disabled:text-faint"
                >
                  {extending ? "Extending…" : "Extend by 24 hours"}
                </button>
              )}
            </span>
          )}
          {!replay.transcriptAttributionComplete && (
            <span>
              Two visits shared a room during this window, so some recordings
              may belong to another patient.
            </span>
          )}
          {pimsPrunedSince && (
            <span>
              I no longer have PIMS activity older than{" "}
              {formatDate(pimsPrunedSince)}.
            </span>
          )}
        </div>
      )}

      {/* Scrubber */}
      <div className="border-b border-rule px-4 py-3">
        <ReplayTimeline
          replay={replay}
          scale={scale}
          playhead={playhead}
          onSeek={seekTo}
          visibleCategories={visibleCategories}
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="flex flex-wrap items-center gap-1.5">
            {ALL_CATEGORIES.map((category) => {
              const active = visibleCategories.has(category);
              return (
                // Word + swatch: the chip row doubles as the legend for the
                // scrubber's category-inked ticks (WEB-3366).
                <button
                  key={category}
                  onClick={() => toggleCategory(category)}
                  aria-pressed={active}
                  className={cn(
                    "flex cursor-pointer items-center gap-1.5 rounded-full border px-2 py-0.5 text-annotation-caps",
                    "transition-colors motion-reduce:transition-none",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
                    active
                      ? cn("bg-card text-ink", CATEGORY_INK[category].border)
                      : "border-rule text-annotation hover:text-ink",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "size-1.5 shrink-0 rounded-full",
                      CATEGORY_INK[category].dot,
                      !active && "opacity-40",
                    )}
                  />
                  {CATEGORY_LABEL[category]}
                </button>
              );
            })}
          </div>
          <span className="text-xs text-annotation">
            {stageName ? `Stage: ${stageName}` : "Not started"}
            {/* No stays at all means the visit was never checked into a room —
                absent location data, not a known "between rooms" state. */}
            {roomName
              ? ` · In ${roomName}`
              : replay.stays.length === 0
                ? " · No room data"
                : " · Not in a room"}
          </span>
        </div>
      </div>

      {/* Feed + live transcript */}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-r border-rule">
          {/* min-h matches TranscriptPanel's header (its icon button sets the
              height there), so the two panes' head rules stay one line. */}
          <div className="flex min-h-11 items-center border-b border-rule px-3 text-annotation-caps text-annotation">
            Moments
          </div>
          <MomentFeed moments={moments} playhead={playhead} onSeek={seekTo} />
        </div>
        {transcriptOpen ? (
          <div className="flex w-96 shrink-0 flex-col">
            <TranscriptPanel
              turns={replay.turns}
              capture={replay.capture}
              playhead={playhead}
              onSeek={seekTo}
              onCollapse={() => setTranscriptOpen(false)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setTranscriptOpen(true)}
            aria-expanded={false}
            aria-label="Show transcript"
            title="Show transcript"
            className="flex w-9 shrink-0 cursor-pointer flex-col items-center gap-2 pt-3 text-annotation transition-colors hover:bg-recess hover:text-ink motion-reduce:transition-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              aria-hidden="true"
              className="text-xs"
            />
            <span
              className="text-annotation-caps"
              style={{ writingMode: "vertical-rl" }}
            >
              Transcript
            </span>
          </button>
        )}
      </div>
    </div>
  );
};
